import React, { useMemo, useRef, useState } from 'react';

import * as THREE from 'three';

import { createPortal, extend, useFrame, useThree } from '@react-three/fiber';
import { OrthographicCamera, useCamera, useTexture } from '@react-three/drei';

import { Edges } from './Edges';

extend(THREE);

const TOP = {
    offsetFactor: {
        x: 0,
        y: 0,
        z: 1,
    },
    axisAngle: {
        x: 0,
        y: 0,
        z: 0,
    },
};

const BOTTOM = {
    offsetFactor: {
        x: 0,
        y: 0,
        z: -1,
    },
    axisAngle: {
        x: Math.PI,
        y: 0,
        z: 0,
    },
};

const FRONT = {
    offsetFactor: {
        x: 0,
        y: -1,
        z: 0,
    },
    axisAngle: {
        x: Math.PI / 2,
        y: 0,
        z: 0,
    },
};

const BACK = {
    offsetFactor: {
        x: 0,
        y: 1,
        z: 0,
    },
    axisAngle: {
        x: -(Math.PI / 2),
        y: 0,
        z: Math.PI,
    },
};

const LEFT = {
    offsetFactor: {
        x: -1,
        y: 0,
        z: 0,
    },
    axisAngle: {
        x: Math.PI / 2,
        y: -(Math.PI / 2),
        z: 0,
    },
};

const RIGHT = {
    offsetFactor: {
        x: 1,
        y: 0,
        z: 0,
    },
    axisAngle: {
        x: Math.PI / 2,
        y: Math.PI / 2,
        z: 0,
    },
};

export const Viewcube = () => {
    const { gl, scene, camera, size } = useThree();
    const virtualScene = useMemo(() => new THREE.Scene(), []);
    const virtualCam = useRef<THREE.OrthographicCamera | any>();
    const ref = useRef<THREE.Mesh>();
    const [hover, set] = useState<number | null>(null);
    const matrix = new THREE.Matrix4();

    const texture_front = useTexture('/textures/front.jpg');
    const texture_front_hover = useTexture('/textures/front-hover.jpg');
    const texture_back = useTexture('/textures/back.jpg');
    const texture_back_hover = useTexture('/textures/back-hover.jpg');
    const texture_right = useTexture('/textures/right.jpg');
    const texture_right_hover = useTexture('/textures/right-hover.jpg');
    const texture_left = useTexture('/textures/left.jpg');
    const texture_left_hover = useTexture('/textures/left-hover.jpg');
    const texture_top = useTexture('/textures/top.jpg');
    const texture_top_hover = useTexture('/textures/top-hover.jpg');
    const texture_bottom = useTexture('/textures/bottom.jpg');
    const texture_bottom_hover = useTexture('/textures/bottom-hover.jpg');

    useFrame(() => {
        matrix.copy(camera.matrix).invert();
        ref?.current?.quaternion.setFromRotationMatrix(matrix);
        gl.autoClear = true;
        gl.render(scene, camera);
        gl.autoClear = false;
        gl.clearDepth();
        gl.render(virtualScene, virtualCam?.current);
    }, 1);

    const top_ = () => {
        const { offsetFactor, axisAngle } = TOP;

        const offsetUnit = camera.position.length();
        const offset = new THREE.Vector3(
            offsetUnit * offsetFactor.x,
            offsetUnit * offsetFactor.y,
            offsetUnit * offsetFactor.z
        );

        const center = new THREE.Vector3();
        const finishPosition = center.add(offset);

        camera.position.set(finishPosition.x, finishPosition.y, finishPosition.z);
        // const positionTween = new TWEEN.Tween(cameraRef.current.position)
        //   .to(finishPosition, 300)
        //   .easing(TWEEN.Easing.Circular.Out);

        const euler = new THREE.Euler(axisAngle.x, axisAngle.y, axisAngle.z);

        // rotate camera too!
        const finishQuaternion = new THREE.Quaternion().copy(camera.quaternion).setFromEuler(euler);

        camera.quaternion.set(finishQuaternion.x, finishQuaternion.y, finishQuaternion.z, finishQuaternion.w);
        // const quaternionTween = new TWEEN.Tween(cameraRef.current.quaternion)
        //   .to(finishQuaternion, 300)
        //   .easing(TWEEN.Easing.Circular.Out);

        // positionTween.start();
        // quaternionTween.start();
    };

    const bottom = () => {
        const { offsetFactor, axisAngle } = BOTTOM;

        const offsetUnit = camera.position.length();
        const offset = new THREE.Vector3(
            offsetUnit * offsetFactor.x,
            offsetUnit * offsetFactor.y,
            offsetUnit * offsetFactor.z
        );

        const center = new THREE.Vector3();
        const finishPosition = center.add(offset);

        camera.position.set(finishPosition.x, finishPosition.y, finishPosition.z);

        const euler = new THREE.Euler(axisAngle.x, axisAngle.y, axisAngle.z);

        const finishQuaternion = new THREE.Quaternion().copy(camera.quaternion).setFromEuler(euler);

        camera.quaternion.set(finishQuaternion.x, finishQuaternion.y, finishQuaternion.z, finishQuaternion.w);
    };

    const front = () => {
        const { offsetFactor, axisAngle } = FRONT;

        const offsetUnit = camera.position.length();
        const offset = new THREE.Vector3(
            offsetUnit * offsetFactor.x,
            offsetUnit * offsetFactor.y,
            offsetUnit * offsetFactor.z
        );

        const center = new THREE.Vector3();
        const finishPosition = center.add(offset);

        camera.position.set(finishPosition.x, finishPosition.y, finishPosition.z);

        const euler = new THREE.Euler(axisAngle.x, axisAngle.y, axisAngle.z);

        const finishQuaternion = new THREE.Quaternion().copy(camera.quaternion).setFromEuler(euler);

        camera.quaternion.set(finishQuaternion.x, finishQuaternion.y, finishQuaternion.z, finishQuaternion.w);
    };

    const back = () => {
        const { offsetFactor, axisAngle } = BACK;

        const offsetUnit = camera.position.length();
        const offset = new THREE.Vector3(
            offsetUnit * offsetFactor.x,
            offsetUnit * offsetFactor.y,
            offsetUnit * offsetFactor.z
        );

        const center = new THREE.Vector3();
        const finishPosition = center.add(offset);

        camera.position.set(finishPosition.x, finishPosition.y, finishPosition.z);

        const euler = new THREE.Euler(axisAngle.x, axisAngle.y, axisAngle.z);

        const finishQuaternion = new THREE.Quaternion().copy(camera.quaternion).setFromEuler(euler);

        camera.quaternion.set(finishQuaternion.x, finishQuaternion.y, finishQuaternion.z, finishQuaternion.w);
    };

    const left = () => {
        const { offsetFactor, axisAngle } = LEFT;

        const offsetUnit = camera.position.length();
        const offset = new THREE.Vector3(
            offsetUnit * offsetFactor.x,
            offsetUnit * offsetFactor.y,
            offsetUnit * offsetFactor.z
        );

        const center = new THREE.Vector3();
        const finishPosition = center.add(offset);

        camera.position.set(finishPosition.x, finishPosition.y, finishPosition.z);

        const euler = new THREE.Euler(axisAngle.x, axisAngle.y, axisAngle.z);

        const finishQuaternion = new THREE.Quaternion().copy(camera.quaternion).setFromEuler(euler);

        camera.quaternion.set(finishQuaternion.x, finishQuaternion.y, finishQuaternion.z, finishQuaternion.w);
    };

    const right = () => {
        const { offsetFactor, axisAngle } = RIGHT;

        const offsetUnit = camera.position.length();
        const offset = new THREE.Vector3(
            offsetUnit * offsetFactor.x,
            offsetUnit * offsetFactor.y,
            offsetUnit * offsetFactor.z
        );

        const center = new THREE.Vector3();
        const finishPosition = center.add(offset);

        camera.position.set(finishPosition.x, finishPosition.y, finishPosition.z);

        const euler = new THREE.Euler(axisAngle.x, axisAngle.y, axisAngle.z);

        const finishQuaternion = new THREE.Quaternion().copy(camera.quaternion).setFromEuler(euler);

        camera.quaternion.set(finishQuaternion.x, finishQuaternion.y, finishQuaternion.z, finishQuaternion.w);
    };

    return createPortal(
        <>
            <OrthographicCamera ref={virtualCam} makeDefault={false} position={[0, 0, 100]} />
            <mesh
                ref={ref}
                raycast={useCamera(virtualCam)}
                position={[size.width / 2 - 80, size.height / 2 - 80, 0]}
                onClick={e => {
                    const face = e.faceIndex && Math.floor(e.faceIndex / 2);
                    switch (face) {
                        case 0:
                            right();
                            break;
                        case 1:
                            left();
                            break;
                        case 2:
                            back();
                            break;
                        case 3:
                            front();
                            break;
                        case 4:
                            top_();
                            break;
                        case 5:
                            bottom();
                            break;
                        default:
                            return null;
                    }
                }}
                onPointerOut={e => set(null)}
                onPointerMove={e => e?.faceIndex && set(Math.floor(e.faceIndex / 2))}
            >
                <meshBasicMaterial
                    attachArray="material"
                    map={hover === 0 ? texture_right_hover : texture_right}
                    //   color={hover === 0 ? "hotpink" : "white"}
                />
                <meshBasicMaterial
                    attachArray="material"
                    map={hover === 1 ? texture_left_hover : texture_left}
                    //   color={hover === 1 ? "hotpink" : "white"}
                />
                <meshBasicMaterial
                    attachArray="material"
                    map={hover === 2 ? texture_top_hover : texture_top}
                    //   color={hover === 2 ? "hotpink" : "white"}
                />
                <meshBasicMaterial
                    attachArray="material"
                    map={hover === 3 ? texture_bottom_hover : texture_bottom}
                    //   color={hover === 3 ? "hotpink" : "white"}
                />
                <meshBasicMaterial
                    attachArray="material"
                    map={hover === 4 ? texture_front_hover : texture_front}
                    //   color={hover === 4 ? "hotpink" : "white"}
                />
                <meshBasicMaterial
                    attachArray="material"
                    map={hover === 5 ? texture_back_hover : texture_back}
                    //   color={hover === 5 ? "hotpink" : "white"}
                />
                <boxGeometry args={[60, 60, 60]} />
                <Edges scale={1.003} color={0x555555} />
            </mesh>
            <ambientLight intensity={0.2} />
            <pointLight position={[10, 10, 10]} intensity={0.5} />
        </>,
        virtualScene
    ) as JSX.Element;
};
